import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../services/api';
import { motion, AnimatePresence } from 'framer-motion';
import {
  TrendUp, TrendDown, Lightning, Stethoscope, Brain, ArrowRight, CaretDown, CaretUp,
  Pill, Heartbeat, Warning,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/* ── Hook ── */
export function usePersonalIntelligence() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/intelligence/personal`);
      setData(d);
    } catch {}
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  return { data, loading };
}

/* ── What Changed ── */
export const ChangesSection = ({ changes }) => {
  if (!changes || changes.length === 0) return null;
  const SEVERITY = {
    positive: { bg: 'bg-emerald-50', border: 'border-emerald-200/50', text: 'text-emerald-800', icon: TrendUp, iconColor: 'text-emerald-500' },
    attention: { bg: 'bg-amber-50', border: 'border-amber-200/50', text: 'text-amber-800', icon: Warning, iconColor: 'text-amber-500' },
    info: { bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-700', icon: Lightning, iconColor: 'text-slate-400' },
  };
  return (
    <section data-testid="intelligence-changes">
      <h2 className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-3">What's changed</h2>
      <div className="space-y-2">
        {changes.map((c, i) => {
          const s = SEVERITY[c.severity] || SEVERITY.info;
          const Icon = s.icon;
          return (
            <motion.div key={i} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className={`${s.bg} border ${s.border} rounded-xl p-3.5`}>
              <div className="flex items-start gap-3">
                <Icon size={16} weight="duotone" className={`${s.iconColor} mt-0.5 flex-shrink-0`} />
                <div className="flex-1 min-w-0">
                  <p className={`text-sm leading-relaxed ${s.text}`}>{c.message}</p>
                  {c.detail && <p className="text-xs text-slate-500 mt-1">{c.detail}</p>}
                  <p className="text-[10px] text-slate-400 mt-1.5">{c.source}</p>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
};

/* ── Smart Connections ── */
export const ConnectionsSection = ({ connections }) => {
  if (!connections || connections.length === 0) return null;
  const CONF = {
    likely: { label: 'Likely connection', color: 'text-blue-600', bg: 'bg-blue-50' },
    possible: { label: 'Possible connection', color: 'text-slate-500', bg: 'bg-slate-50' },
  };
  return (
    <section data-testid="intelligence-connections">
      <h2 className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-3">Connections noticed</h2>
      <div className="space-y-2">
        {connections.map((c, i) => {
          const conf = CONF[c.confidence] || CONF.possible;
          return (
            <div key={i} className={`${conf.bg} border border-slate-200/50 rounded-xl p-3.5`}>
              <div className="flex items-start gap-3">
                <Brain size={16} weight="duotone" className={`${conf.color} mt-0.5 flex-shrink-0`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-700 leading-relaxed">{c.message}</p>
                  {c.detail && <p className="text-xs text-slate-500 mt-1">{c.detail}</p>}
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className={`text-[10px] ${conf.color}`}>{conf.label}</span>
                    <span className="text-[10px] text-slate-400">{c.source}</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

/* ── Doctor Prep ── */
export const DoctorPrepCard = ({ doctorPrep }) => {
  const [expanded, setExpanded] = useState(false);
  if (!doctorPrep?.items?.length) return null;
  return (
    <section data-testid="intelligence-doctor-prep">
      <button onClick={() => setExpanded(!expanded)}
        className="w-full bg-white border border-slate-150 rounded-xl p-4 text-left hover:bg-slate-50/50 transition-colors">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-[#235347]/8 rounded-lg flex items-center justify-center flex-shrink-0">
            <Stethoscope size={16} weight="duotone" className="text-[#235347]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-0.5">Doctor Prep</p>
            <p className="text-sm text-slate-600">{doctorPrep.items.length} item{doctorPrep.items.length > 1 ? 's' : ''} worth discussing</p>
            {doctorPrep.summary && <p className="text-[10px] text-slate-400 mt-0.5">{doctorPrep.summary}</p>}
          </div>
          {expanded ? <CaretUp size={16} className="text-slate-300" /> : <CaretDown size={16} className="text-slate-300" />}
        </div>
      </button>
      <AnimatePresence>
        {expanded && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden">
            <div className="mt-2 space-y-1.5">
              {doctorPrep.items.map((item, i) => (
                <div key={i} className={`bg-white border border-slate-150 rounded-xl p-3 ${item.is_test ? 'border-l-2 border-l-blue-300' : ''}`}>
                  <p className="text-sm text-slate-700">{item.text}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-[10px] text-slate-400">{item.reason}</span>
                    <span className="text-[10px] text-slate-300">·</span>
                    <span className="text-[10px] text-slate-400">{item.source}</span>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

/* ── Memory Context ── */
export const MemoryContext = ({ notes }) => {
  if (!notes || notes.length === 0) return null;
  const ICONS = { context: Heartbeat, pattern: Lightning, medication: Pill };
  return (
    <section data-testid="intelligence-memory">
      <h2 className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-3">Kindra remembers</h2>
      <div className="space-y-1.5">
        {notes.map((n, i) => {
          const Icon = ICONS[n.type] || Brain;
          return (
            <div key={i} className="flex items-start gap-2.5 py-2">
              <Icon size={14} weight="duotone" className="text-slate-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm text-slate-600 leading-relaxed">{n.text}</p>
                <p className="text-[10px] text-slate-400 mt-0.5">{n.source}</p>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};
